import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { forkJoin } from 'rxjs';
import { IconName } from '@core/constants/icons';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AppButtonDirective } from '@shared/ui/button/app-button.directive';
import { CheckboxComponent } from '@shared/ui/checkbox/checkbox.component';
import { ModalComponent } from '@shared/ui/modal/modal.component';
import { ModalHeaderComponent } from '@shared/ui/modal/components/modal-header.component';
import { ModalFooterComponent } from '@shared/ui/modal/components/modal-footer.component';
import { Plataforma, VersaoHistoricoItem } from '../versao.model';
import { VersaoService } from '../versao.service';
import { interpretarNotas, NotasNaVersao } from '../notas-versao';
import { NotasDaVersaoComponent } from './notas-da-versao.component';
import { NotasMarkdownComponent } from './notas-markdown.component';

/** Um bloco de novidades de uma plataforma, com a versão dela. */
interface BlocoPlataforma {
  plataforma: Plataforma;
  titulo: string;
  complemento: string;
  icone: IconName;
  versao: string;
  notas: NotasNaVersao;
  markdown: string | null;
}

/** Versão já interpretada para exibição no modo histórico. */
interface VersaoHistorico {
  versao: string;
  publicadoEm: string;
  notas: NotasNaVersao;
  markdown: string | null;
}

/** Rótulos de cada plataforma — o leitor precisa saber o que é dele e o que é do campo. */
const IDENTIDADE: Record<Plataforma, { titulo: string; complemento: string; icone: IconName }> = {
  web: { titulo: 'Na web', complemento: 'onde você está agora', icone: 'monitor' },
  app: { titulo: 'No aplicativo', complemento: 'usado pela equipe em campo', icone: 'smartphone' },
};

/**
 * Modal de "Novidades da versão". Separa o que mudou na web do que mudou no
 * aplicativo, cada bloco com a sua própria versão. Tem dois modos:
 * - `atual` (automático, pós-login): mostra só as plataformas com novidade ainda
 *   não vista, com checkbox "Não mostrar novamente".
 * - `historico` (manual, via menu/perfil): lista todas as versões publicadas da
 *   plataforma escolhida, mais recente no topo, sem checkbox.
 *
 * O texto das notas é desenhado por app-notas-markdown quando a versão tem Markdown (escrito no
 * backoffice), e por interpretarNotas e app-notas-da-versao quando não tem.
 */
@Component({
  selector: 'app-novidades-versao',
  imports: [
    ModalComponent,
    ModalHeaderComponent,
    ModalFooterComponent,
    CheckboxComponent,
    FormsModule,
    DatePipe,
    IconComponent,
    AppButtonDirective,
    NotasDaVersaoComponent,
    NotasMarkdownComponent,
  ],
  templateUrl: './novidades-versao.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NovidadesVersaoComponent implements OnInit {
  private readonly _service = inject(VersaoService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly aberto = signal(false);
  protected readonly modo = signal<'atual' | 'historico'>('atual');
  protected readonly blocos = signal<BlocoPlataforma[]>([]);
  protected readonly historicoWeb = signal<VersaoHistorico[]>([]);
  protected readonly historicoApp = signal<VersaoHistorico[]>([]);
  protected readonly plataformaHistorico = signal<Plataforma>('web');
  protected naoMostrarNovamente = true;

  /** Diz de cara o alcance do que vem abaixo, sem repetir a versão (ela vive em cada bloco). */
  protected readonly descricao = computed(() => {
    const plataformas = this.blocos().map((bloco) => bloco.plataforma);
    if (plataformas.length > 1) return 'O que mudou na web e no aplicativo';
    return plataformas[0] === 'app' ? 'O que mudou no aplicativo' : 'O que mudou na web';
  });

  protected readonly historicoAtual = computed(() =>
    this.plataformaHistorico() === 'app' ? this.historicoApp() : this.historicoWeb(),
  );

  /** O seletor só faz sentido quando existe histórico do app para alternar. */
  protected readonly mostrarSeletorPlataforma = computed(() => this.historicoApp().length > 0);

  /** Abas do histórico — a web vem primeiro, é a tela de quem está lendo. */
  protected readonly abasHistorico = (['web', 'app'] as const).map((plataforma) => ({
    plataforma,
    titulo: IDENTIDADE[plataforma].titulo,
    icone: IDENTIDADE[plataforma].icone,
  }));

  ngOnInit(): void {
    // Automático: só aparece quando alguma plataforma tem versão nova ainda não vista.
    this.carregarAtual();
    // Sob demanda (menu/perfil): reabre em modo histórico.
    this._service.abrir$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this.carregarHistorico());
  }

  private carregarAtual(): void {
    this._service.obterVersoes().subscribe({
      next: ({ web, app }) => {
        const blocos: BlocoPlataforma[] = [];

        if (this._service.deveMostrarWeb(web)) {
          blocos.push(this.montarBloco('web', web.ultimaVersao ?? '', web.notas, web.notasMarkdown));
        }
        if (this._service.deveMostrarApp(app)) {
          blocos.push(this.montarBloco('app', this._service.identificarVersaoApp(app), app.notas, app.notasMarkdown));
        }
        if (!blocos.length) return;

        this.modo.set('atual');
        this.naoMostrarNovamente = true;
        this.blocos.set(blocos);
        this.aberto.set(true);
      },
      error: () => {
        // Novidades não é crítico: se o endpoint falhar, não atrapalha o uso do sistema.
      },
    });
  }

  private montarBloco(
    plataforma: Plataforma,
    versao: string,
    notas: string | null,
    markdown: string | null,
  ): BlocoPlataforma {
    return { plataforma, ...IDENTIDADE[plataforma], versao, notas: interpretarNotas(notas), markdown };
  }

  private carregarHistorico(): void {
    forkJoin({
      web: this._service.obterHistorico('web'),
      app: this._service.obterHistorico('app'),
    }).subscribe({
      next: ({ web, app }) => {
        this.modo.set('historico');
        this.historicoWeb.set(web.map((item) => this.mapearHistorico(item)));
        this.historicoApp.set(app.map((item) => this.mapearHistorico(item)));
        this.plataformaHistorico.set('web');
        this.aberto.set(true);
      },
      error: () => {
        // Novidades não é crítico: se o endpoint falhar, não atrapalha o uso do sistema.
      },
    });
  }

  private mapearHistorico(item: VersaoHistoricoItem): VersaoHistorico {
    return {
      versao: item.versao,
      publicadoEm: item.publicadoEm,
      notas: interpretarNotas(item.notas),
      markdown: item.notasMarkdown,
    };
  }

  protected trocarPlataforma(plataforma: Plataforma): void {
    this.plataformaHistorico.set(plataforma);
  }

  fechar(): void {
    if (this.modo() === 'atual' && this.naoMostrarNovamente) {
      // Marca vista só o que foi realmente exibido — assim a web não é "consumida"
      // quando o modal abriu apenas por causa do app, e vice-versa.
      for (const bloco of this.blocos()) {
        this._service.marcarVista(bloco.plataforma, bloco.versao);
      }
    }
    this.aberto.set(false);
  }
}
